import moment from 'moment';
import { entityStore, getBinLevelMeasurementOverrides, Measurements } from '~/utility/BinSentry-ui-utility';
import { nothing, styleMap, unsafeCSS } from '~/lib/bn-lit-element';
import { css, html, LitEntity, renderIf } from '../../../utility/lit-entity';
import { StoreMixin } from '../../../utility/store-mixin';
import { sharedStyles } from '../../../styles/shared-styles';
import { LevelColours } from '../../../utility/level-colours-mixin';
import { levelChartStyles } from './level-chart-styles';
import { SmoothLevelHistoryChart } from './smooth-level-history-chart';
import _ from 'lodash';
import SirenParse from 'siren-parser';

class BinLevelSmoothHistoryGraph extends StoreMixin(LitEntity) {
  constructor() {
    super();
    this.points = [];
    this.start = moment().startOf('day').utc().subtract(3, 'week').format();
    this.end = moment().add(1, 'week').utc().format();
    this.maxFutureViewDate = moment().add(2, 'week').utc().format();
    this.history = true;
    this.measurement = null;
    this.showRaw = null;
    this.statsLoaded = false;
    this.levelsLoaded = false;
    this.axesReady = false;
    this.dataReady = false;
    this.zoomY = false;
    this.levelListenerMap = {};
    this.levelEntitiesList = [];

    this._onResize = this._onResize.bind(this);
    this._onKeyPress = this._onKeyPress.bind(this);
  }

  connectedCallback() {
    super.connectedCallback?.();
    window.addEventListener('resize', this._onResize);
    window.addEventListener('keydown', this._onKeyPress);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('keydown', this._onKeyPress);
    this.levelEntitiesList = [];
    this._updateListeners();
  }

  static get properties() {
    return {
      start: { type: String },
      end: { type: String },
      hideAxes: { type: Boolean, attribute: 'hide-axes' },
      hideToolbar: { type: Boolean, attribute: 'hide-toolbar' },
      hideOptions: { type: Boolean, attribute: 'hide-options' },
      hideLegend: { type: Boolean, attribute: 'hide-legend' },
      heightOverride: { type: String },
      history: { type: Boolean, reflect: true },
      forecast: { type: Boolean, reflect: true },
      points: { type: Array, attribute: false },
      role: { type: String, attribute: false },
      totalVolume: { type: Number, attribute: false },
      levelsHref: { type: String, attribute: false },
      statsHref: { type: String, attribute: false },
      propertiesHref: { type: String, attribute: false },
      // we can't just check for presence of the entities because stats could give back a 204 which leads to
      // an undefined entity.  So instead we track whether each is loaded
      levelsLoaded: { type: Boolean, attribute: false },
      statsLoaded: { type: Boolean, attribute: false },
      propertiesLoaded: { type: Boolean, attribute: false },
      noData: { type: Boolean, attribute: false },
      showRaw: { type: Boolean, attribute: 'show-raw' },
      axesReady: { type: Boolean, attribute: false },
      dataReady: { type: Boolean, attribute: false },
      measurement: { type: Object, attribute: false },
      contentLabel: { type: String, attribute: 'content-label', default: 'feed' },
      highlightSelection: { type: Boolean, attribute: 'highlight-selection' },
      highlightManualFeedMask: { type: Boolean, attribute: 'highlight-manual-feed-mask' },
      focus: { type: String },
      zoomY: { type: Boolean, attribute: 'zoom-y' },
    };
  }

  static get styles() {
    return [sharedStyles, levelChartStyles, css`
      .focusLine,
      .forecast-region {
        pointer-events: none;
      }

      .options {
        display: flex;
        justify-content: flex-end;
        margin: 0 40px;
      }

      .toolbar {
        display: flex;
        justify-content: space-between;
        flex-direction: row;
        align-items: center;
        margin-left: 60px;
        margin-right: 40px;
        margin-top: 1rem;
      }
      .toolbar .date-range-picker {
        display: flex;
        justify-content: center;
      }
      .toolbar .date-range {
        white-space: nowrap;
        display: flex;
        align-items: center;
        padding: 0 8px;
      }

      .legend {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
      }
      .legend > div {
        margin-right: 1rem;
        display: flex;
        align-items: center;
      }
      .legend > div:last-child {
        margin-right: 0;
      }
      .legend .label {
        flex-grow: 1;
        font-size: .9em;
      }
      .legend .preview {
        border-radius: 3px;
        height: .8rem;
        min-width: .8rem;
        float: left;
        margin: 2px 6px 2px 0;
        display: flex;
      }
      .legend .history .preview {
        display: flex;
      }
      .legend .history .preview div {
        height: 100%;
        width: 10px;
        display: inline-block;
      }
      .legend .history .preview div:first-of-type {
        border-radius: 3px 0 0 3px;
      }
      .legend .history .preview div:last-of-type {
        border-radius: 0 3px 3px 0;
      }

      .legend .history .preview .high {
        background: ${unsafeCSS(LevelColours.getThresholdByName('high').darkColour)};
      }
      .legend .history .preview .medium {
        background: ${unsafeCSS(LevelColours.getThresholdByName('medium').darkColour)};
      }
      .legend .history .preview .low {
        background: ${unsafeCSS(LevelColours.getThresholdByName('low').darkColour)};
      }
      .legend .forecast .preview {
        background: var(--blue-60);
      }
      .raw-toggle input {
        vertical-align: middle;
      }
      .raw-toggle span {
        vertical-align: middle;
      }

      @media(max-width: 600px) {
        .options {
          display: none;
        }
      }
      @media(max-width: 960px) {
        .toolbar {
          flex-direction: column-reverse;
          justify-content: stretch;
          margin-left: 0;
          margin-right: 0;
          margin-top: 0;
        }
      }
      :host([small]) .toolbar {
        margin: 0;
      }
    `];
  }

  render() {
    return html`
      <custom-date-range-entity
        .href="${this.levelsHref}"
        .start="${this.start}"
        .end="${this.end}"
        @rangeupdate="${this._onUpdateLevels}"
      ></custom-date-range-entity>

      <lit-entity .href="${this.statsHref}" @update="${this._onUpdateStats}"></lit-entity>
      <lit-entity .href="${this.propertiesHref}" @update="${this._onUpdateProperties}"></lit-entity>

      ${renderIf(this.role === 'ingredient', () => html`
        <bn-button variant="text" @click=${() => this.zoomY = !this.zoomY} style="position: relative; z-index: 1; --bn-button-width: 8em;">
          <bn-icon name="far fa-search${this.zoomY ? '-minus' : '-plus'}"></bn-icon>
          ${this.zoomY ? 'Zoom Out' : 'Zoom In'}
        </bn-button>
      `)}

      ${renderIf(this.role !== 'ingredient' && !this.hideOptions, () => html`
        <div class="options">
          <label class="raw-toggle">
            <input type="checkbox" ?checked=${this.showRaw} @input="${(value) => this._onToggleRaw(value)}" />
            <span>Raw readings</span>
          </label>
        </div>
      `)}

      <div
        id="graph-container"
        class="chart ${this.showRaw || this.role === 'ingredient' ? 'show-raw' : ''} ${this.noData ? 'no-data' : ''} ${!this.dataReady ? 'loading' : ''}"
        style=${styleMap({ ...this.heightOverride ? { height: this.heightOverride } : {} })}
      ></div>

      ${renderIf(!this.hideToolbar, () => html`
        <div class="toolbar">
          <div class="date-range-picker">
            <button class="button button--text" @click="${this._prev}">&lt; Older</button>
            <span class="date-range">
              ${moment(this.start).local().format('MMM D')} - ${moment(this.end).local().format('MMM D')}
            </span>
            <button class="button button--text" @click="${this._next}" ?disabled=${this.end >= this.maxFutureViewDate}>Newer &gt;</button>
          </div>
          ${renderIf(this.highlightSelection, () => html`
            <div class="level-picker">
              <bn-button variant="text" @click="${this._prevLevel}">&lt; Previous level</bn-button>
              <bn-button variant="text" @click="${this._nextLevel}">Next level &gt;</bn-button>
            </div>
          `)}
          ${this._renderLegend()}
        </div>
      `)}
    `;
  }

  _renderLegend() {
    if (this.hideLegend) {
      return nothing;
    }

    const items = [
      html`
        <div class="history">
          <div class="preview">
            <div class="high"></div>
            <div class="medium"></div>
            <div class="low"></div>
          </div>
          <span class="label">Past ${this.contentLabel} level</span>
        </div>
      `,
      (this.forecast && this.statsEntity) && html`
        <div class="forecast">
          <div class="preview"></div>
          <span class="label">Predicted ${this.contentLabel} level</span>
        </div>
      `,
    ];
    return html`<div class='legend'>${items}</div>`;
  }

  updated(changedProperties) {
    if (changedProperties.get('href')) {
      this.points = [];
      this.levelsHref = null;
      this.statsHref = null;
      this.propertiesHref = null;
      this.levelsEntity = null;
      this.statsEntity = null;
      this.propertiesEntity = null;
    }

    if (changedProperties.has('levelsHref')) {
      this.levelsLoaded = false;
    }

    if (changedProperties.has('statsHref')) {
      this.statsLoaded = false;
    }

    if (changedProperties.has('propertiesHref')) {
      this.propertiesLoaded = false;
    }

    this.axesReady = !!((this.totalVolume || this.points.length) && this.start && this.end && this.measurement);
    this.dataReady = !!(
      (!this.levelsHref || this.levelsLoaded)
      && (this.role === 'ingredient' ||
       (!this.statsHref || this.statsLoaded)
       && this.propertiesLoaded
      )
      && this.measurement
    );

    // only once both sub entities have had a chance to load do we decide if there is no data
    this.noData = this.dataReady && this.points.length === 0;

    if (changedProperties.has('measurement')) {
      this._updateGraph();
    }

    const axesChange = changedProperties.has('axesReady') && this.axesReady;
    const dataChange = (changedProperties.has('dataReady') || changedProperties.has('points')) && this.dataReady;
    if (axesChange || dataChange || changedProperties.has('zoomY')) {
      this._draw();
    }

    if (changedProperties.has('hideToolbar')) {
      this._onResize();
    }
  }

  get element() {
    return this.shadowRoot.querySelector('#graph-container');
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);
    this.levelsHref = this.getHref('https://api.binsentry.com/rel/v2/bin-levels');
    this.statsHref = this.getHref('https://api.binsentry.com/rel/bin-stat-latest');
    this.propertiesHref = this.getHref('https://api.binsentry.com/rel/bin-level-bin-properties');
    this.totalVolume = this.getProperty('volumes.fullTotal');
    this.bulkDensity = this.getProperty('bulkDensity');
    this.measurement = Measurements.get('binLevel', await getBinLevelMeasurementOverrides(this.entity));
    this.role = this.getProperty('role');
  }

  _stateChanged({ userFeatures }) {
    // don't change if this store update is coming after the user has explicitly chosen to show/not show raw
    this.showRaw = this.role === 'ingredient' || (this.showRaw ?? userFeatures?.rawBinLevelsByDefault);
  }

  _onResize() {
    this.chart && this.chart.resize();
  }

  _onKeyPress(e) {
    const input = e.composedPath().filter((n) => n.tagName === 'INPUT')[0];
    if (input) {
      return;
    }

    if (e.key === 'ArrowLeft') {
      this._prevLevel();
    } else if (e.key === 'ArrowRight') {
      this._nextLevel();
    }
  }

  async _onUpdateLevels(e) {
    this.levelsEntity = e.detail.entity;
    this.levelEntitiesList = this.levelsEntity?.entities || [];
    this.levelsLoaded = true;
    this._updateListeners();
    this._updateGraph();
  }

  async _onUpdateStats(e) {
    this.statsEntity = e.detail.entity;
    this.statsLoaded = true;
    this._updateGraph();
  }

  async _onUpdateProperties(e) {
    this.propertiesEntity = e.detail.entity;
    this.propertiesLoaded = true;
    this._updateGraph();
  }

  _prevLevel() {
    this.chart?.selectPrevious();
  }

  _nextLevel() {
    this.chart?.selectNext();
  }

  _prev() {
    const oldStart = this.start;
    const oldEnd = this.end;
    const hours = Math.round(moment(oldEnd).diff(moment(oldStart), 'hours') * .75);
    this.start = moment(oldStart).subtract(hours, 'hours').format();
    this.end = moment(oldEnd).subtract(hours, 'hours').format();
    this.levelsLoaded = false;
  }

  _next() {
    const oldStart = this.start;
    const oldEnd = this.end;
    const hours = Math.round(moment(oldEnd).diff(moment(oldStart), 'hours') * .75);
    this.start = moment(oldStart).add(hours, 'hours').format();
    this.end = moment(oldEnd).add(hours, 'hours').format();
    this.levelsLoaded = false;
  }

  _getHistory() {
    const levels = this.levelEntitiesList;
    const smoothingDisabled = this.propertiesEntity?.properties.smoothingModel === 'none';
    if (!this.history || !levels) {
      return [];
    }
    const points = [];
    const { bulkDensity, totalVolume } = this;
    const convert = (volume) => this.measurement.convert({ volume, bulkDensity, totalVolume });
    levels.forEach((level) => {
      const { publishedAt, volume } = level.properties;
      const id = level.getLinkByRel('self').href;
      const currentEstimate = smoothingDisabled ? undefined : level.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-estimate-current');
      const initialEstimate = level.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-estimate-initial');
      const feedMask = level.getSubEntity('https://api.binsentry.com/rel/manual-feed-mask-data');
      points.push({
        ...level.properties,
        id,
        value: convert(volume),
        estimatedValue: currentEstimate && convert(currentEstimate.properties.estimatedVolume),
        initialEstimatedValue: initialEstimate && convert(initialEstimate.properties.estimatedVolume),
        date: moment(publishedAt),
        feedMask,
      });
    });
    points.sort((a, b) => a.date.diff(b.date));
    return points;
  }

  _getForecast() {
    if (!this.forecast || !this.statsEntity) {
      return [];
    }
    const points = [];
    const { forecast } = this.statsEntity.properties.volume;
    if (!forecast) {
      return [];
    }
    const { bulkDensity, totalVolume } = this;
    forecast.forEach((f) => {
      const date = moment(f.publishedAt);
      if (date.isAfter(this.end) || date.isBefore(this.start)) {
        return;
      }
      const volume = f.low;
      const value = this.measurement.convert({ volume, bulkDensity, totalVolume });
      const weight = volume * bulkDensity;
      points.push({ value, date, volume, weight, forecast: true });
    });
    return points;
  }

  _updateListeners() {
    const levelIds = this.levelEntitiesList.map(level => level.getLink('self').href);
    const existingListenerKeys = Object.keys(this.levelListenerMap || {});
    const newListenerKeys = _.difference(levelIds, existingListenerKeys);
    const removeListenerKeys = _.difference(existingListenerKeys, levelIds);

    const listenerFunction = ({ entity: rawEntity }) => {
      const entity = SirenParse(rawEntity);
      const key = entity.getLink('self').href;
      const index = _.findIndex(this.levelEntitiesList, e => e.getLink('self').href === key);
      if (index >= 0) {
        this.levelEntitiesList.splice(index, 1, entity);
        this._updateGraph();
      }
    };
    newListenerKeys.forEach(key => {
      this.levelListenerMap[key] = entityStore.addEntityListener(key, listenerFunction);
    });
    removeListenerKeys.forEach(key => {
      entityStore.removeEntityListener(key, this.levelListenerMap[key]);
      delete this.levelListenerMap[key];
    });
  }

  _updateGraph() {
    if (!this.measurement) {
      this.points = [];
      return;
    }
    const history = this._getHistory();
    const lastHistory = history.length > 0 && history[history.length - 1];
    let forecast = this._getForecast();
    if (lastHistory && forecast.length) {
      // only include forecasts that are after the last history (+1 hour)
      forecast = forecast.filter((p) => p.date - lastHistory.date > 3.6e6);
    }
    this.points = [...history, ...forecast];
  }

  _onToggleRaw() {
    this.showRaw = !this.showRaw;
  }

  _draw() {
    const { measurement } = this;
    const maxValue = Math.max(...this.points.map(p => p.value)) || 0;
    const upperLimit = measurement.metric === 'ratio' ? 100 : measurement.convert({ volume: this.totalVolume, bulkDensity: this.bulkDensity });
    const yMax = Math.max(maxValue, upperLimit) * 1.05;
    const yRange = {
      start: 0,
      end: yMax,
    };
    if (this.zoomY) {
      const values = this.points.filter(p => p.label !== 'OUTLIER').map(p => p.value);
      const high = Math.max(...values, 0);
      const low = Math.min(...values, high);
      // if the range is too small, the 2% margin of error will look like movement
      // that isn't really there, so we ensure the range is at least 5% of the total volume
      const range = Math.max(high - low, this.totalVolume * 0.05);
      const padding = range * 0.5;
      yRange.start = Math.max(0, low - padding);
      yRange.end = Math.min(high + padding, upperLimit);
    }
    const selectedDate = this.chart?.selectedPoint?.date || this.focus;
    this.chart = new SmoothLevelHistoryChart({
      onSelect: (point) => this.dispatchEvent(new CustomEvent('bn:select-data-point', { detail: { point } })),
      element: this.element,
      hideAxes: this.hideAxes,
      points: this.dataReady ? this.points : [],
      unit: measurement.unit,
      xRange: {
        start: this.start,
        end: this.end,
      },
      yRange,

      ...this.role === 'ingredient' ? {
        timeScale: 'hours',
        hideLine: true,
        hideRawToggle: true,
        hideRefills: true,
      } : {
        timeScale: 'days',
      },

      highlightWidth: 10,
      highlightSelection: this.highlightSelection,
      highlightManualFeedMask: this.highlightManualFeedMask,

      // while we can grow the y-axis to scale to unexpectedly out-of-range values, the colours should be computed
      // solely based on the bin capacity to make it consistent with the bin profile (unless upperLimit is 0 to avoid NaN)
      fullValue: upperLimit || yMax,
    });
    // if focus is present, and in range, select it
    if (selectedDate) {
      const inRange = moment(selectedDate).isBetween(moment(this.start), moment(this.end));
      if (inRange) {
        this.chart.selectByDate(selectedDate);
      }
    }
  }
}

window.customElements.define('bin-level-smooth-history-graph', BinLevelSmoothHistoryGraph);
